import React, { useState } from "react";
import { aqiMeta, pm25Color, MiniBar } from "./aqmHelpers";

const VIEWS = ["24h AQI Trend", "Pollutant Breakdown", "Wind Correlation", "AQI Heatmap"];

// 7-day × 24-hour fake heatmap data
const HEATMAP_HOURS = ["00","04","08","12","16","20"];
const HEATMAP_DAYS  = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const heatmapAqi = [
  [85,78,115,148,165,168],[92,80,118,152,172,175],[75,70,98,128,145,148],
  [98,88,128,168,185,182],[105,95,140,175,195,188],[65,58,88,112,125,118],
  [52,48,72,88,98,92],
];

const AqmPollutionPanel = ({ d }) => {
  const [view, setView] = useState("24h AQI Trend");
  const { hourlyTrend, pollutionAnalysis } = d;
  const maxAqi = Math.max(...hourlyTrend.map(x => x.aqi));

  const maxWind = Math.max(...pollutionAnalysis.windCorrelation.map(w => w.aqiAvg));

  return (
    <div className="aqm-pollution-panel">
      <div className="aqm-view-row">
        {VIEWS.map(v => (
          <button key={v} className={`aqm-view-btn ${view === v ? "aqm-view-active" : ""}`} onClick={() => setView(v)}>{v}</button>
        ))}
      </div>

      {/* 24h AQI Trend */}
      {view === "24h AQI Trend" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">24-Hour City AQI Trend</div>
          <div className="aqm-bar-chart" style={{ height: 220 }}>
            {hourlyTrend.map(pt => {
              const m = aqiMeta(pt.aqi);
              return (
                <div key={pt.t} className="aqm-bar-col">
                  <div className="aqm-bar-val" style={{ fontSize: 8, color: m.color }}>{pt.aqi > 140 ? pt.aqi : ""}</div>
                  <div className="aqm-bar-wrap">
                    <div className="aqm-bar-fill" style={{ height: `${(pt.aqi / maxAqi) * 100}%`, background: m.color }} />
                  </div>
                  <div className="aqm-bar-lbl">{pt.t.split(":")[0]}</div>
                </div>
              );
            })}
          </div>
          <div className="aqm-trend-note">⚠ Evening peak (17:00–18:00): AQI 185 — Traffic + Industrial combined effect during SW wind period.</div>

          {/* PM2.5 + NO2 mini chart */}
          <div className="aqm-section-title" style={{ marginTop: 16 }}>PM2.5 & NO₂ Co-trend</div>
          <div className="aqm-do-chart">
            {hourlyTrend.map((pt, i) => (
              <div key={i} className="aqm-do-col">
                <div className="aqm-do-bar-wrap">
                  <div className="aqm-do-bar" style={{ height: `${(pt.pm25 / 110) * 100}%`, background: pm25Color(pt.pm25) }} />
                </div>
                <div className="aqm-do-lbl">{pt.t.split(":")[0]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pollutant Breakdown */}
      {view === "Pollutant Breakdown" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">Pollution Source Apportionment</div>
          <div className="aqm-source-grid">
            {pollutionAnalysis.primarySources.map(s => (
              <div key={s.source} className="aqm-src-card" style={{ borderTopColor: s.color }}>
                <div className="aqm-src-pct" style={{ color: s.color }}>{s.contribution}%</div>
                <div className="aqm-src-lbl">{s.source}</div>
                <div style={{ marginTop: 6 }}>
                  <MiniBar value={s.contribution} max={50} color={s.color} height={8} />
                </div>
              </div>
            ))}
          </div>
          <div className="aqm-section-title" style={{ marginTop: 16 }}>Pollutant Levels vs NAAQS Limits</div>
          {[
            { label: "PM2.5",  val: d.summary.pm25, limit: 60,  unit: "µg/m³" },
            { label: "PM10",   val: d.summary.pm10, limit: 100, unit: "µg/m³" },
            { label: "NO₂",   val: d.summary.no2,  limit: 80,  unit: "µg/m³" },
            { label: "SO₂",   val: d.summary.so2,  limit: 80,  unit: "µg/m³" },
            { label: "CO",     val: d.summary.co,   limit: 2,   unit: "mg/m³" },
            { label: "O₃",    val: d.summary.o3,   limit: 100, unit: "µg/m³" },
          ].map(p => {
            const pct = (p.val / p.limit) * 100;
            const col = pct > 100 ? "#ef4444" : pct > 80 ? "#f59e0b" : "#10b981";
            return (
              <div key={p.label} className="aqm-pollutant-row">
                <div className="aqm-pr-label">{p.label}</div>
                <div style={{ flex: 1, padding: "0 12px" }}>
                  <MiniBar value={Math.min(p.val, p.limit * 1.2)} max={p.limit * 1.2} color={col} height={10} />
                </div>
                <span style={{ color: col, fontWeight: 700, fontSize: 13, minWidth: 80 }}>{p.val} {p.unit}</span>
                <span style={{ fontSize: 11, color: "#9ca3af", minWidth: 70 }}>Limit: {p.limit}</span>
                <span className="aqm-badge" style={{ background: col + "18", color: col, fontSize: 10 }}>{pct > 100 ? "BREACH" : pct > 80 ? "Near" : "OK"}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Wind Correlation */}
      {view === "Wind Correlation" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">AQI vs Wind Direction Correlation</div>
          <div className="aqm-section-sub">Average AQI by wind direction — identifies pollution source sectors</div>
          <div className="aqm-wind-chart">
            {pollutionAnalysis.windCorrelation.map(w => {
              const m = aqiMeta(w.aqiAvg);
              return (
                <div key={w.windDir} className="aqm-wc-row">
                  <div className="aqm-wc-dir">{w.windDir}</div>
                  <div style={{ flex: 1 }}>
                    <MiniBar value={w.aqiAvg} max={maxWind} color={m.color} height={12} />
                  </div>
                  <div style={{ color: m.color, fontWeight: 700, minWidth: 36 }}>{w.aqiAvg}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", minWidth: 160 }}>{w.note}</div>
                </div>
              );
            })}
          </div>
          <div className="aqm-ai-strip" style={{ marginTop: 12 }}>
            🤖 AI: SW wind direction correlates with AQI 185 (highest) — industrial zone upwind. NW/N winds bring cleanest air (AQI 82–95). Pollution advisory should auto-trigger when wind shifts to SW.
          </div>
        </div>
      )}

      {/* AQI Heatmap */}
      {view === "AQI Heatmap" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">Weekly AQI Heatmap (Day × Time of Day)</div>
          <div className="aqm-section-sub">Identify peak pollution windows at a glance</div>
          <div className="aqm-heatmap">
            <div className="aqm-hm-row aqm-hm-header">
              <div className="aqm-hm-day-lbl" />
              {HEATMAP_HOURS.map(h => <div key={h} className="aqm-hm-hour-lbl">{h}:00</div>)}
            </div>
            {HEATMAP_DAYS.map((day, di) => (
              <div key={day} className="aqm-hm-row">
                <div className="aqm-hm-day-lbl">{day}</div>
                {heatmapAqi[di].map((aqi, hi) => {
                  const m = aqiMeta(aqi);
                  return (
                    <div key={hi} className="aqm-hm-cell" style={{ background: m.color, opacity: 0.7 + (aqi / 300) * 0.3 }} title={`${day} ${HEATMAP_HOURS[hi]}:00 — AQI ${aqi}`}>
                      <span className="aqm-hm-aqi">{aqi}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="aqm-trend-note">Fri 16:00–20:00 shows consistently highest AQI (185–195) — corresponds to traffic peak + industrial end-of-shift emissions.</div>
        </div>
      )}
    </div>
  );
};

export default AqmPollutionPanel;
